import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UserPlus, Users, Building2, ShieldCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useMeta } from "@/context/MetaContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { StatusChip } from "@/components/StatusChip";
import { ROLE_LABELS } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Admin() {
  const qc = useQueryClient();
  const { data: meta } = useMeta();
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const { data: employees } = useQuery({ queryKey: ["employees-all"], queryFn: async () => (await api.get("/employees")).data });

  const [userOpen, setUserOpen] = useState(false);
  const [empOpen, setEmpOpen] = useState(false);
  const [uForm, setUForm] = useState({ name: "", email: "", password: "", role: "manager", campus: meta?.campuses?.[0] || "" });
  const [eForm, setEForm] = useState({ name: "", title: "", campus: meta?.campuses?.[0] || "", manager_id: "" });

  const createUser = useMutation({
    mutationFn: async () => api.post("/users", uForm),
    onSuccess: () => { toast.success("User created"); setUserOpen(false); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e) => toast.error(apiError(e)),
  });
  const createEmp = useMutation({
    mutationFn: async () => api.post("/employees", eForm),
    onSuccess: () => { toast.success("Employee added"); setEmpOpen(false); qc.invalidateQueries({ queryKey: ["employees-all"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  const managers = (users || []).filter((u) => u.role === "manager");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-patria-ink">People & Users</h1>
        <p className="text-patria-muted mt-1">Manage users, roles, employees, and campuses.</p>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-patria-surface border border-patria-muted/15">
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="campuses" data-testid="tab-campuses">Campuses</TabsTrigger>
        </TabsList>

        {/* USERS */}
        <TabsContent value="users" className="mt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={userOpen} onOpenChange={setUserOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-patria-terracotta hover:bg-patria-terracottaDeep text-patria-surface" data-testid="add-user-button"><UserPlus className="h-4 w-4 mr-2" /> Add user</Button>
              </DialogTrigger>
              <DialogContent className="bg-patria-surface">
                <DialogHeader><DialogTitle className="font-heading">Add user</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-1.5"><Label>Name</Label><Input value={uForm.name} onChange={(e) => setUForm({ ...uForm, name: e.target.value })} data-testid="user-name-input" className="bg-patria-surface border-patria-muted/30" /></div>
                  <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={uForm.email} onChange={(e) => setUForm({ ...uForm, email: e.target.value })} data-testid="user-email-input" className="bg-patria-surface border-patria-muted/30" /></div>
                  <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={uForm.password} onChange={(e) => setUForm({ ...uForm, password: e.target.value })} data-testid="user-password-input" placeholder="Set an initial password" className="bg-patria-surface border-patria-muted/30" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Role</Label>
                      <Select value={uForm.role} onValueChange={(v) => setUForm({ ...uForm, role: v })}>
                        <SelectTrigger className="bg-patria-surface border-patria-muted/30" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-patria-surface">
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="people_ops">People Operations</SelectItem>
                          <SelectItem value="leadership">Executive Leadership</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label>Campus</Label>
                      <Select value={uForm.campus} onValueChange={(v) => setUForm({ ...uForm, campus: v })}>
                        <SelectTrigger className="bg-patria-surface border-patria-muted/30" data-testid="user-campus-select"><SelectValue placeholder="Campus" /></SelectTrigger>
                        <SelectContent className="bg-patria-surface">{meta?.campuses?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <DialogFooter><Button onClick={() => createUser.mutate()} disabled={createUser.isPending || !uForm.name.trim() || !uForm.email.trim() || uForm.password.length < 6} className="rounded-full bg-patria-green text-patria-surface" data-testid="user-save-button">Create user</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="bg-patria-surface border-patria-muted/15 divide-y divide-patria-muted/10" data-testid="users-list">
            {(users || []).map((u) => (
              <div key={u.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-patria-ink">{u.name}</p>
                  <p className="text-xs text-patria-muted">{u.email} · {u.campus}</p>
                </div>
                <StatusChip label={ROLE_LABELS[u.role]} tone={u.role === "leadership" ? "red" : u.role === "people_ops" ? "amber" : "green"} />
              </div>
            ))}
          </Card>
        </TabsContent>

        {/* EMPLOYEES */}
        <TabsContent value="employees" className="mt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={empOpen} onOpenChange={setEmpOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-patria-terracotta hover:bg-patria-terracottaDeep text-patria-surface" data-testid="add-employee-button"><UserPlus className="h-4 w-4 mr-2" /> Add employee</Button>
              </DialogTrigger>
              <DialogContent className="bg-patria-surface">
                <DialogHeader><DialogTitle className="font-heading">Add employee</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-1.5"><Label>Name</Label><Input value={eForm.name} onChange={(e) => setEForm({ ...eForm, name: e.target.value })} data-testid="emp-name-input" className="bg-patria-surface border-patria-muted/30" /></div>
                  <div className="space-y-1.5"><Label>Title</Label><Input value={eForm.title} onChange={(e) => setEForm({ ...eForm, title: e.target.value })} data-testid="emp-title-input" className="bg-patria-surface border-patria-muted/30" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Campus</Label>
                      <Select value={eForm.campus} onValueChange={(v) => setEForm({ ...eForm, campus: v })}>
                        <SelectTrigger className="bg-patria-surface border-patria-muted/30" data-testid="emp-campus-select"><SelectValue placeholder="Campus" /></SelectTrigger>
                        <SelectContent className="bg-patria-surface">{meta?.campuses?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label>Manager</Label>
                      <Select value={eForm.manager_id} onValueChange={(v) => setEForm({ ...eForm, manager_id: v })}>
                        <SelectTrigger className="bg-patria-surface border-patria-muted/30" data-testid="emp-manager-select"><SelectValue placeholder="Manager" /></SelectTrigger>
                        <SelectContent className="bg-patria-surface">{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <DialogFooter><Button onClick={() => createEmp.mutate()} disabled={createEmp.isPending} className="rounded-full bg-patria-green text-patria-surface" data-testid="emp-save-button">Add employee</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="bg-patria-surface border-patria-muted/15 divide-y divide-patria-muted/10" data-testid="employees-list">
            {(employees || []).map((e) => (
              <div key={e.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-patria-ink">{e.name}</p>
                  <p className="text-xs text-patria-muted">{e.title} · {e.campus}</p>
                </div>
                <span className="text-xs text-patria-muted">Mgr: {e.manager_name}</span>
              </div>
            ))}
          </Card>
        </TabsContent>

        {/* CAMPUSES */}
        <TabsContent value="campuses" className="mt-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="campuses-list">
            {meta?.campuses?.map((c) => (
              <Card key={c} className="bg-patria-surface border-patria-muted/15 p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-patria-green/10 flex items-center justify-center"><Building2 className="h-5 w-5 text-patria-green" /></div>
                <span className="font-medium text-patria-ink">{c}</span>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
